import { SearchForRetail } from '@/client/api';
import { getConfig } from '@/config';
import { formatTenantSettingsResponse } from '@/tenant/tenant.formatters';
import { getTenantName } from '@/utils';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/**
 * Builds the tenant settings API URL path.
 *
 * Uses the current tenant name from localStorage with a fallback to
 * TARGET_CUSTOMER if no tenant is set (e.g., during initial load).
 *
 * Note: The URL uses tenant with fallback, while ccBaseQuery's X-Groupby-Customer-Id
 * header only sets when tenant exists. This is intentional as the backend uses the
 * URL path as the primary tenant identifier.
 *
 * @internal Exported for testing purposes only
 * @returns URL path in format: /tenant/{customerId}/settings
 */
export function buildTenantSettingsUrl() {
    var _a;
    const customerId = ((_a = getTenantName()) === null || _a === void 0 ? void 0 : _a.trim()) || getConfig().customer;
    return `/${SearchForRetail.TENANT_PATH}/${customerId}/${SearchForRetail.SETTINGS_PATH}`;
}
/**
 * RTK Query endpoints for tenant settings.
 *
 * This module extends the base ccApi with tenant settings-related endpoints.
 * It uses the injectEndpoints pattern for code splitting.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const tenantSettingsApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches tenant settings for the current customer.
         *
         * Uses getTenantName() with fallback to TARGET_CUSTOMER to determine
         * the customer ID, matching the existing tenant.client.ts pattern.
         *
         * The response is transformed using formatTenantSettingsResponse to
         * ensure auth0Region validation.
         */
        getTenantSettings: builder.query({
            query: buildTenantSettingsUrl,
            transformResponse: formatTenantSettingsResponse,
            providesTags: [CC_API_TAGS.TenantSettings],
        }),
    }),
});
export const { useGetTenantSettingsQuery, useLazyGetTenantSettingsQuery } = tenantSettingsApi;
